import { useEffect, useState } from "react";
import { Star, Loader2, Trash2, Eye, EyeOff, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

export default function ReviewsPanel({ onCountChange }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      const { data } = await apiClient.get("/admin/reviews?limit=200");
      setItems(data);
      onCountChange?.(data.length);
    } catch (e) {
      toast.error("Could not load reviews.");
    }
  }

  async function toggle(review, field) {
    setBusyId(review.review_id);
    try {
      const { data } = await apiClient.patch(`/admin/reviews/${review.review_id}`, {
        [field]: !review[field],
      });
      setItems((prev) => (prev || []).map((r) => (r.review_id === data.review_id ? data : r)));
      toast.success(`${field === "featured" ? "Featured" : "Approval"} updated.`);
    } catch (e) {
      toast.error("Update failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(review) {
    if (!window.confirm(`Delete this review from ${review.display_name}?`)) return;
    setBusyId(review.review_id);
    try {
      await apiClient.delete(`/admin/reviews/${review.review_id}`);
      setItems((prev) => (prev || []).filter((r) => r.review_id !== review.review_id));
      toast.success("Review deleted.");
    } catch (e) {
      toast.error("Delete failed.");
    } finally {
      setBusyId(null);
    }
  }

  if (items === null) {
    return (
      <div className="border border-white/10 bg-[#0A0A0A] p-16 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#E60000]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-reviews-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
          Featured reviews appear on the homepage &middot; toggle the star to promote
        </div>
        <button
          data-testid="admin-reviews-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold hover:border-[#E60000] transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-white/10 bg-[#0A0A0A] p-16 text-center">
          <Star className="w-10 h-10 text-zinc-600 mx-auto" />
          <h3 className="font-display text-2xl font-bold mt-4">No reviews yet.</h3>
          <p className="text-zinc-400 mt-2 text-sm">
            Customers can leave a review from the tracking page once their booking is Collected or Delivered.
          </p>
        </div>
      ) : (
        <div className="border border-white/10 bg-[#0A0A0A] divide-y divide-white/5" data-testid="admin-reviews-list">
          {items.map((r) => {
            const busy = busyId === r.review_id;
            return (
              <div
                key={r.review_id}
                data-testid={`admin-review-${r.review_id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 items-start"
              >
                <div className="md:col-span-2">
                  <div className="font-display font-bold text-white">{r.display_name}</div>
                  <div className="text-xs text-zinc-500 mt-1">Ref: {r.booking_ref}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : ""}
                  </div>
                </div>
                <div className="md:col-span-1">
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < r.rating ? "text-[#E60000] fill-[#E60000]" : "text-zinc-700"}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="md:col-span-6 text-sm text-zinc-300 italic">
                  &ldquo;{r.comment}&rdquo;
                  {r.route && <div className="not-italic text-xs text-zinc-500 mt-2">{r.route}</div>}
                </div>
                <div className="md:col-span-3 flex justify-end items-center gap-2 flex-wrap">
                  <button
                    onClick={() => toggle(r, "featured")}
                    disabled={busy}
                    data-testid={`admin-review-${r.review_id}-feature`}
                    className={`inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1.5 border transition-colors disabled:opacity-40 ${
                      r.featured
                        ? "border-[#E60000] text-white bg-[#E60000]/10"
                        : "border-white/15 text-zinc-300 hover:border-[#E60000]"
                    }`}
                  >
                    <Star className={`w-3 h-3 ${r.featured ? "fill-[#E60000] text-[#E60000]" : ""}`} />
                    {r.featured ? "Featured" : "Feature"}
                  </button>
                  <button
                    onClick={() => toggle(r, "approved")}
                    disabled={busy}
                    data-testid={`admin-review-${r.review_id}-approve`}
                    className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1.5 border border-white/15 text-zinc-300 hover:border-[#E60000] transition-colors disabled:opacity-40"
                  >
                    {r.approved ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    {r.approved ? "Visible" : "Hidden"}
                  </button>
                  <button
                    onClick={() => remove(r)}
                    disabled={busy}
                    data-testid={`admin-review-${r.review_id}-delete`}
                    className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1.5 border border-white/15 text-zinc-500 hover:border-[#E60000] hover:text-[#E60000] transition-colors disabled:opacity-40"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
